import React, { useRef, useState } from 'react'
import { uploadFiles } from './AppFileUploader.helper'
import { getTenantId } from 'utils/getTenantId'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { UserFile, UserFileTag } from '__generated__/api-types-and-hooks'
import { AppUploadImageIcon } from 'components/Common/AppSvgIcons/AppUploadImageIcon'
import GradientCircleNotchIcon from 'components/Common/SvgIcons/GradientCircleNotchIcon'
import { AppFileCard } from 'components/Common/AppFileCard/AppFileCard.component'
import { MAX_FILE_SIZE } from 'constants/file'

interface IAppFileUploaderProps {
  maxFiles?: number
  allowedFormats?: string[]
  maxFileSize?: number // in MB
  onUploadCompleted?: (uploadedFiles: UserFile[] | null) => void
  isFilesSaving?: boolean
  descriptionText?: string
  fileTag?: UserFileTag
}

export const AppFileUploader: React.FC<IAppFileUploaderProps> = ({
  onUploadCompleted,
  maxFiles = 10,
  allowedFormats = ['png', 'jpg', 'pdf', 'mkv', 'mp4', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'],
  maxFileSize,
  isFilesSaving,
  descriptionText,
  fileTag,
}) => {
  const tenantId = getTenantId()
  const userId = useSelector((state: RootState) => state.user.user.id)
  const clientId = useSelector((state: RootState) => state.clients.client.profile.id)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<Map<number, number>>(new Map())
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([])
  const [uploadErrors, setUploadErrors] = useState<Map<number, string>>(new Map())
  const [abortControllers, setAbortControllers] = useState<AbortController[]>([])

  const reindexMapAfterRemoval = <T,>(prev: Map<number, T>, removedIndex: number) => {
    const next = new Map<number, T>()
    prev.forEach((value, key) => {
      if (key < removedIndex) next.set(key, value)
      else if (key > removedIndex) next.set(key - 1, value)
    })
    return next
  }

  const removeFileAtIndex = (fileIndex: number, opts: { abort?: boolean } = {}) => {
    if (opts.abort) abortControllers[fileIndex]?.abort()

    setUploadingFiles((prev) => prev.filter((_, idx) => idx !== fileIndex))
    setUploadProgress((prev) => reindexMapAfterRemoval(prev, fileIndex))
    setUploadErrors((prev) => reindexMapAfterRemoval(prev, fileIndex))
    setAbortControllers((prev) => prev.filter((_, idx) => idx !== fileIndex))
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    const droppedFiles = Array.from(e.dataTransfer.files)
    processFiles(droppedFiles)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    processFiles(selectedFiles)
  }

  const processFiles = (newFiles: File[]) => {
    setError(null)

    // Check file sizes FIRST
    const oversizedFiles = new Map<number, string>()
    const validSizeFiles: File[] = []

    newFiles.forEach((file, index) => {
      if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.set(index, 'File is too big')
      } else {
        validSizeFiles.push(file)
      }
    })

    const fileExtensions = validSizeFiles.map((file) => file.name?.split('.').pop())
    validSizeFiles.forEach((file) =>
      console.log(
        file.type,
        allowedFormats.some((format) => fileExtensions?.includes(format))
      )
    )
    // Filter by file type if allowedFormats specified
    const validFiles =
      allowedFormats.length > 0
        ? validSizeFiles.filter((file) =>
            allowedFormats.some((format) => fileExtensions?.includes(format))
          )
        : validSizeFiles

    if (validFiles.length < validSizeFiles.length) {
      setError(`Some files are not supported. Please check the file types and try again.`)
      fileInputRef.current && (fileInputRef.current.value = '')
      return
    }

    // Filter by custom maxFileSize if provided
    const sizeValidFiles = maxFileSize
      ? validFiles.filter((file) => file.size <= maxFileSize * 1024 * 1024)
      : validFiles

    if (sizeValidFiles.length < validFiles.length) {
      setError(`Some files exceed the maximum size limit of ${maxFileSize}MB`)
      fileInputRef.current && (fileInputRef.current.value = '')
      return
    }

    // Limit number of files
    if (maxFiles && sizeValidFiles.length > maxFiles) {
      setError(`You can only upload up to ${maxFiles} files.`)
      fileInputRef.current && (fileInputRef.current.value = '')
      return
    }

    const finalFiles = sizeValidFiles.slice(0, maxFiles)

    // Handle oversized files separately - show them with error state
    if (oversizedFiles.size > 0) {
      setUploadingFiles(newFiles)
      setUploadErrors(oversizedFiles)
      setIsUploading(false)
    }

    // Upload valid files
    if (finalFiles.length > 0) {
      handleUpload(finalFiles, oversizedFiles)
    }
  }

  const handleUpload = async (
    filesToUpload: File[],
    errorFiles: Map<number, string> = new Map()
  ) => {
    try {
      setIsUploading(true)
      setUploadingFiles((prev) => (errorFiles.size > 0 ? prev : filesToUpload))
      setUploadProgress(new Map())
      setUploadErrors(errorFiles)

      // Create abort controllers for each file
      const controllers = filesToUpload.map(() => new AbortController())
      setAbortControllers(controllers)

      const uploadedFiles = await uploadFiles({
        files: filesToUpload,
        tenantId,
        clientId,
        userId,
        fileTag,
        onProgress: (fileIndex, percentage) => {
          setUploadProgress((prev) => {
            const newMap = new Map(prev)
            newMap.set(fileIndex, percentage)
            return newMap
          })
        },
        abortControllers: controllers,
        onError: (fileIndex, errorMessage) => {
          setUploadErrors((prev) => {
            const newMap = new Map(prev)
            newMap.set(fileIndex, 'Failed to upload')
            return newMap
          })
        },
      })

      // Only call onUploadCompleted if there are successful uploads
      if (uploadedFiles.length > 0) {
        onUploadCompleted?.(
          uploadedFiles.map((file) => ({ ...file, createdAt: new Date().toISOString() }))
        )
      }
    } catch (error) {
      console.error(error)
      onUploadCompleted?.(null)
    } finally {
      setIsUploading(false)
      // Don't clear state if there are errors (keep files visible for retry)
      if (uploadErrors.size === 0) {
        setUploadingFiles([])
        setUploadProgress(new Map())
        setAbortControllers([])
      }
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleCancelUpload = (fileIndex: number) => {
    removeFileAtIndex(fileIndex, { abort: true })
  }

  const handleRetryUpload = (fileIndex: number) => {
    // Clear error for this file
    setUploadErrors((prev) => {
      const newMap = new Map(prev)
      newMap.delete(fileIndex)
      return newMap
    })

    // Retry upload for this specific file
    const fileToRetry = uploadingFiles[fileIndex]
    if (fileToRetry) {
      // Clear the current file from the list first
      setUploadingFiles((prev) => prev.filter((_, idx) => idx !== fileIndex))
      // Then upload
      handleUpload([fileToRetry])
    }
  }

  return (
    <div className="w-full">
      <div
        className={`border-[1.5px] border-dashed border-app-grey-40 rounded-lg p-8 text-center cursor-pointer
          ${isDragging ? 'bg-gray-50' : 'bg-white'}`}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-input')?.click()}
      >
        <div className="flex flex-col items-center gap-2">
          <AppUploadImageIcon />
          <p className="text-black-appDark font-inter text-sm">
            {descriptionText ?? 'Drop your file here'}, or{' '}
            <span className="text-purple-strong">click to browse</span>
          </p>
          <p className="text-xs text-primary-disabledTextLight">
            {allowedFormats.map((format) => '.' + format).join(', ')}{' '}
            {maxFileSize ? `up to ${maxFileSize}MB` : ''}.
          </p>
        </div>
      </div>

      <input
        id="file-input"
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
        accept={allowedFormats.join(',')}
        ref={fileInputRef}
      />

      {(isUploading || uploadErrors.size > 0) && uploadingFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          {uploadingFiles.map((file, index) => {
            const tempFile: UserFile = {
              id: `temp-${index}`,
              name: file.name,
              size: file.size.toString(),
              url: '',
              type: file.type,
            }
            return (
              <AppFileCard
                key={`${file.name}-${index}`}
                file={tempFile}
                showRemove
                isUploading={!uploadErrors.has(index)}
                uploadProgress={uploadProgress.get(index) || 0}
                uploadError={uploadErrors.get(index)}
                onCancel={() => handleCancelUpload(index)}
                onRetry={() => handleRetryUpload(index)}
                onRemove={() => removeFileAtIndex(index)}
              />
            )
          })}
        </div>
      )}

      {isFilesSaving && !isUploading && (
        <div className="mt-4 space-y-2">
          <div className="flex justify-center items-center gap-2">
            <GradientCircleNotchIcon width="30" height="30" />
            <span className="text-sm font-inter">Saving...</span>
          </div>
        </div>
      )}

      {error && <div className="mt-4 text-red text-xs font-inter">{error}</div>}
    </div>
  )
}
